<template>
    <section class="acknowledgee-input-container" :class="isModalDisplay ? 'rd-mt-3' : 'rd-mt-6'">
        <span class="rd-flex rd-font-bold" :class="isModalDisplay ? 'rd-mb-2.5' : 'rd-mb-4'">{{
            component.customData.acknowledgeeInformationLabel }}</span>

        <section class="input-container">
            <div class="form-row" :class="{ 'md:rd-gap-x-4': fieldStyle != FieldStyle.Compact }">
                <ValidatedInput :id="component.id" :value="renderStore.tribute.acknowledgeeFirstName" label="First Name"
                    class="rd-w-full md:rd-flex-equal" name="acknowledgeeFirstName"
                    :isRequired="component.customData.hasAcknowledgement" :maxLength="75">
                    <template #input="inputProps">
                        <input type="text" v-model="renderStore.tribute.acknowledgeeFirstName" placeholder="First Name"
                            :aria-describedby="inputProps.describedBy" aria-required="true"
                            :aria-invalid="inputProps.isInvalid" :id="component.id + '_acknowledgeeFirstName'" />
                    </template>
                </ValidatedInput>

                <ValidatedInput :id="component.id" :value="renderStore.tribute.acknowledgeeLastName" label="Last Name"
                    class="rd-w-full md:rd-flex-equal" name="acknowledgeeLastName"
                    :isRequired="component.customData.hasAcknowledgement" :maxLength="75">
                    <template #input="inputProps">
                        <input type="text" v-model="renderStore.tribute.acknowledgeeLastName" placeholder="Last Name"
                            :aria-describedby="inputProps.describedBy" aria-required="true"
                            :aria-invalid="inputProps.isInvalid" :id="component.id + '_acknowledgeeLastName'" />
                    </template>
                </ValidatedInput>
            </div>
        </section>

        <section class="input-container" v-if="hasBothAcknowledgementMethods"
            :class="[{ '!rd-mt-4': isModalDisplay }, { 'rd-mb-2': acknowledgementMethod === AcknowledgementMethod.Postal && isModalDisplay }, {}]">
            <fieldset class="rd-mt-1 rd-inline-flex form-radio-group">
                <legend class="rd-block rd-text-sm rd-font-medium rd-leading-none"
                    :class="isModalDisplay ? 'rd-mb-2.5' : 'rd-mb-4'">How would you like your acknowledgement to be
                    sent?
                </legend>
                <VueRadioButton id="Email" :selected="acknowledgementMethod === AcknowledgementMethod.Email"
                    :value=AcknowledgementMethod.Email name="Email" v-model="acknowledgementMethod" labelText="Email"
                    :is-admin="false" />

                <VueRadioButton id="Postal" :selected="acknowledgementMethod === AcknowledgementMethod.Postal"
                    :value=AcknowledgementMethod.Postal class="rd-leading-none rd-ml-5" name="Postal"
                    v-model="acknowledgementMethod" labelText="Postal" :is-admin="false" />
            </fieldset>
        </section>

        <section v-if="acknowledgementMethod === AcknowledgementMethod.Email" class="input-container">
            <div class="form-row" :class="{ 'md:rd-gap-x-4': fieldStyle != FieldStyle.Compact }">
                <ValidatedInput :id="component.id" :value="renderStore.tribute.acknowledgeeEmailAddress"
                    label="Email Address" name="tributeAcknowledgeeEmailAddress" class="rd-w-full md:rd-flex-equal"
                    :isRequired="component.customData.hasAcknowledgement && acknowledgementMethod === AcknowledgementMethod.Email"
                    :isEmail="true">
                    <template #input="inputProps">
                        <input type="text" :id="component.id + '_tributeAcknowledgeeEmailAddress'"
                            placeholder="Email Address" :aria-describedby="inputProps.describedBy" aria-required="true"
                            :aria-invalid="inputProps.isInvalid"
                            v-model="renderStore.tribute.acknowledgeeEmailAddress" />
                    </template>
                </ValidatedInput>
            </div>
        </section>

        <section v-if="acknowledgementMethod === AcknowledgementMethod.Postal" class="input-container">
            <div class="form-row" :class="{ 'md:rd-gap-x-4': fieldStyle != FieldStyle.Compact }">
                <AddressContainer :id="component.id + '_acknowledgeeAddress'"
                    :passedAddress="renderStore.tribute.acknowledgeeAddress"
                    :required="component.customData.hasAcknowledgement && acknowledgementMethod === AcknowledgementMethod.Postal"
                    name="Acknowledgee Address" address1Label="Acknowledgee Address"
                    address1Placeholder="Address Line 1" address2Placeholder="Address Line 2" cityPlaceholder="City"
                    statePlaceholder="State" postalPlaceholder="Postal Code" countryPlaceholder="Country"
                    @update-address="updateAddress" />
            </div>
        </section>

        <section class="input-container">
            <div class="form-row md:rd-gap-x-4">
                <ValidatedInput :id="component.id" :value="renderStore.tribute.message" class="rd-w-full"
                    label="Message to Recipient" name="Tribute.Message" :isRequired="false">
                    <template #input="inputProps">
                        <textarea v-model="renderStore.tribute.message" placeholder="Message"
                            :aria-describedby="inputProps.describedBy" :id="component.id + '_tributeMessage'"
                            resize="none" rows="4"></textarea>
                    </template>
                </ValidatedInput>
            </div>
        </section>
    </section>
</template>

<script lang="ts">
import AddressContainer from "@/builder/components/input/AddressContainer.vue";
import ValidatedInput from "@/builder/components/input/ValidatedInput.vue";
import { FieldStyle, PartialDisplayType } from "@/builder/enums";
import { useRenderStore } from "@/builder/stores/renderStore";
import VueRadioButton from '@/components/shared/form/radio/RadioButton.vue';
import { PropType, Ref, computed, defineComponent, inject } from "vue";
import { Address } from "../../../interfaces";
import { AcknowledgementMethod } from '../../enums';
import { PageDesign } from "../page/PageInterface";
import { ITributeComponent } from "./TributeInterface";

export default defineComponent({
    name: "TributeInputComponent",
    props: {
        component: {
            type: Object as PropType<ITributeComponent>,
            required: true
        },
    },
    components: {
        VueRadioButton,
        ValidatedInput,
        AddressContainer
    },
    setup() {
        const renderStore = useRenderStore();
        const pageDesign = inject("pageDesign") as Ref<PageDesign>;
        const fieldStyle = computed(() => {
            return pageDesign.value.fieldStyle;
        });
        return { renderStore, AcknowledgementMethod, fieldStyle, FieldStyle };
    },
    data() {
        return {
            acknowledgementMethod: AcknowledgementMethod.Email,
        }
    },
    computed: {
        hasBothAcknowledgementMethods(): boolean {
            return this.component.customData.hasEmailAcknowledgement && this.component.customData.hasPostalAcknowledgement;
        },

        isModalDisplay() {
            return this.renderStore.page.partialDisplayType === PartialDisplayType.Modal;
        },
    },
    methods: {
        updateAddress(address: Address) {
            this.renderStore.tribute.acknowledgeeAddress = address;
        },
    },
    mounted() {
        //set acknowledgement method
        if (this.component.customData.hasEmailAcknowledgement && this.component.customData.hasPostalAcknowledgement) {
            this.acknowledgementMethod = AcknowledgementMethod.Email;
        }
        else {
            if (this.component.customData.hasEmailAcknowledgement) {
                this.acknowledgementMethod = AcknowledgementMethod.Email;
            }
            else if (this.component.customData.hasPostalAcknowledgement) {
                this.acknowledgementMethod = AcknowledgementMethod.Postal;
            }
        }
    },
    watch: {

        hasBothAcknowledgementMethods: {
            immediate: true,
            handler(newVal: boolean) {
                if (!newVal) {
                    //editor selection changed, set the acknowledgement method to the only one available
                    if (this.component.customData.hasEmailAcknowledgement) {
                        this.renderStore.tribute.sendByEmail = true;
                        this.acknowledgementMethod = AcknowledgementMethod.Email;
                    }
                    else if (this.component.customData.hasPostalAcknowledgement) {
                        this.renderStore.tribute.sendByPostal = true;
                        this.acknowledgementMethod = AcknowledgementMethod.Postal;
                    }
                }
                else {
                    //both are available, set to email
                    this.renderStore.tribute.sendByEmail = true;
                    this.acknowledgementMethod = AcknowledgementMethod.Email;
                }
            }
        },

        acknowledgementMethod: function (newVal: AcknowledgementMethod) {
            if (newVal === AcknowledgementMethod.Email) {
                this.renderStore.tribute.sendByEmail = true;
                this.renderStore.tribute.sendByPostal = false;

                //clear out postal fields
                if (this.renderStore.tribute.acknowledgeeAddress) {
                    this.renderStore.tribute.acknowledgeeAddress.address1 = null;
                    this.renderStore.tribute.acknowledgeeAddress.city = null;
                    this.renderStore.tribute.acknowledgeeAddress.state = null;
                    this.renderStore.tribute.acknowledgeeAddress.postal = null;
                    this.renderStore.tribute.acknowledgeeAddress.countryString = null;
                }
            }
            else if (newVal === AcknowledgementMethod.Postal) {
                this.renderStore.tribute.sendByEmail = false;
                this.renderStore.tribute.sendByPostal = true;

                //clear out email field
                this.renderStore.tribute.acknowledgeeEmailAddress = null;
            }
        },
    }
});
</script>
